import type { FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import type { PartnerAdminView } from '../../api.js';

interface PartnerAdminsTabProps {
  admins: PartnerAdminView[];
  adminEmail: string;
  adminName: string;
  adminPassword: string;
  busy: boolean;
  onAdminEmailChange: (value: string) => void;
  onAdminNameChange: (value: string) => void;
  onAdminPasswordChange: (value: string) => void;
  onGrantAdmin: (event: FormEvent) => void;
  onRevokeAdmin: (userId: string) => void;
}

export function PartnerAdminsTab({
  admins,
  adminEmail,
  adminName,
  adminPassword,
  busy,
  onAdminEmailChange,
  onAdminNameChange,
  onAdminPasswordChange,
  onGrantAdmin,
  onRevokeAdmin
}: PartnerAdminsTabProps) {
  const { t } = useTranslation();
  return (
    <section className="panel">
      <h3>{t('partners.admins')}</h3>
      <ul className="plain-list">
        {admins.map((admin) => (
          <li key={admin.id}>
            <span>{admin.name ? `${admin.name} · ${admin.email}` : admin.email}</span>
            <button
              onClick={() => {
                if (window.confirm(t('partners.confirmRevokeAdmin', { email: admin.email })))
                  onRevokeAdmin(admin.id);
              }}
              disabled={busy || admins.length === 1}
              title={admins.length === 1 ? t('partners.lastAdminHint') : undefined}
            >
              {t('partners.revoke')}
            </button>
          </li>
        ))}
        {admins.length === 0 && <li className="muted">{t('partners.noAdmins')}</li>}
      </ul>
      <form className="toolbar-form" onSubmit={onGrantAdmin} autoComplete="off">
        <input
          placeholder={t('partners.adminEmail')}
          value={adminEmail}
          onChange={(event) => onAdminEmailChange(event.target.value)}
          autoComplete="off"
        />
        <input
          placeholder={t('partners.adminName')}
          value={adminName}
          onChange={(event) => onAdminNameChange(event.target.value)}
          autoComplete="off"
        />
        <input
          type="password"
          placeholder={t('partners.adminPassword')}
          value={adminPassword}
          onChange={(event) => onAdminPasswordChange(event.target.value)}
          autoComplete="new-password"
        />
        <button disabled={!adminEmail.trim() || !adminPassword || busy}>{t('partners.grantAdmin')}</button>
      </form>
    </section>
  );
}
